import { ReactNode, useEffect, useState } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from "react-router-dom";
import {
  init,
  replayIntegration,
  reactRouterV6BrowserTracingIntegration,
  getClient,
  ErrorBoundary,
  setUser,
  setExtras
} from "@sentry/react";

import { ErrorPage } from "~/pages";
import { filterSentryEvent } from "./utils";
import { getClientIdFromStorage, getSentryAnonId, isChunkLoadError } from "~/utils";
import { reportAppCrash } from "~/utils/reportError";

const ENV_IS_ENABLED_SENTRY = import.meta.env.VITE_FEATURE_FLAG_is_enabled_sentry === "true";

// --- Safe init ---
try {
  if (ENV_IS_ENABLED_SENTRY && !getClient()) {
    console.log("sentry init");

    init({
      dsn: import.meta.env.VITE_APP_SENTRY_DSN,
      integrations: [
        reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        }),
        replayIntegration({
          maskAllText: true,
          blockAllMedia: true
        })
      ],
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.2,
      replaysOnErrorSampleRate: 1.0,
      environment: import.meta.env.VITE_APP_ENV,
      release: import.meta.env.VITE_APP_SENTRY_RELEASE,
      ignoreErrors: [/remoteFrameRegistration\.processChildFrameMessage/i],
      denyUrls: [/mc\.yandex\.ru/],
      beforeSend(event, hint) {
        try {
          return filterSentryEvent(event, hint);
        } catch {
          return event;
        }
      }
    });
  }
} catch (err) {
  console.warn("Sentry init failed:", err);
}

interface Props {
  children: ReactNode;
}

const CLIENT_ID_POLL_INTERVAL_MS = 1000;
const CLIENT_ID_POLL_MAX_ATTEMPTS = 15;

export const SentryContrainer = ({ children }: Props) => {
  const [clientId, setClientId] = useState(() => getClientIdFromStorage());

  useEffect(() => {
    if (clientId) return;

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const id = getClientIdFromStorage();

      if (id) {
        setClientId(id);
        clearInterval(interval);
      } else if (attempts >= CLIENT_ID_POLL_MAX_ATTEMPTS) {
        clearInterval(interval);
      }
    }, CLIENT_ID_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [clientId]);

  useEffect(() => {
    const client = getClient();
    if (!client) return;

    try {
      setUser({ id: getSentryAnonId() });
    } catch (err) {
      console.warn("Error applying Sentry user context:", err);
    }
  }, []);

  useEffect(() => {
    const client = getClient();
    if (!client || clientId) return;

    try {
      setExtras({
        clientId: "ID клиента не нашлось, нужно проверить реализацию"
      });
    } catch (err) {
      console.warn("Error applying Sentry extras:", err);
    }
  }, [clientId]);

  useEffect(() => {
    const handleWindowError = (event: ErrorEvent) => {
      const error =
        event.error instanceof Error
          ? event.error
          : new Error(event.message || "Unknown window error");
      reportAppCrash(clientId, error, undefined, undefined, "window_error");
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const error =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === "string" ? reason : "Unhandled promise rejection");
      reportAppCrash(clientId, error, undefined, undefined, "unhandled_rejection");
    };

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [clientId]);

  return (
    <ErrorBoundary
      fallback={({ error }) => <ErrorPage isChunkLoadError={isChunkLoadError(error)} />}
      onError={(error, componentStack, eventId) => {
        reportAppCrash(clientId, error, componentStack, eventId);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
